import { UserInvitationOptionalResponseDto } from "@/lib/interfaces/invitation";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Heading, Spinner } from "@chakra-ui/react";
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../components/root/context";
import useApiRequest from "../../lib/hooks/useRequest";
import { resolveProfileGate } from "../../lib/utils/profile-gate";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/components/ui/dialog";

interface InvitationResponse {
  alreadyHasOrganization: boolean;
  organizationName: string;
}

const Invitation = ({
  isInviteOpen,
  setInviteOpen,
  uuid,
}: {
  isInviteOpen?: boolean;
  setInviteOpen?: Dispatch<SetStateAction<boolean>>;
  uuid?: string;
}) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();
  const { setError } = useContext(Context);
  const [inviteRes, setInviteRes] = useState<InvitationResponse>();
  const [inviteIsLoading, setInviteIsLoading] = useState(true);
  const { isAuthenticated, isLoading } = useAuth0();
  const [invitation, setInvitation] = useState<UserInvitationOptionalResponseDto>();

  const getInvitation = useCallback(async () => {
    const res = await apiRequest<UserInvitationOptionalResponseDto>("organizations/invitations", "GET");
    setInviteIsLoading(false);
    if (res.data) {
      setInvitation(res.data);
    }
  }, [apiRequest]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate("/register");
      return;
    }
    getInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInvitation]);

  const checkInvite = useCallback(async () => {
    if (invitation?.invitation && !window.location.pathname.includes("register")) {
      setInviteRes(invitation.invitation);
    }
  }, [invitation]);

  useEffect(() => {
    if (invitation?.invitation) {
      if (!isLoading && isAuthenticated) {
        checkInvite();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInvite, navigate, invitation]);

  const handleInvite = async (action: "accept" | "decline") => {
    if (invitation?.invitation) {
      const inviteCode = invitation.invitation._id;
      const res = await apiRequest(`organizations/invitations/${inviteCode}/${action}`, "PATCH", {
        toast: {
          toastText: "Einladung wurde angenommen",
        },
      });
      if (res.error) return;

      // Close invite flow and resolve next step based on backend state.
      setError?.(undefined);
      setInviteOpen?.(false);

      if (action === "accept") {
        const gate = await resolveProfileGate({
          apiRequest,
          navigate,
          retry: { attempts: 10, delayMs: 300 },
        });
        if (gate.ok) {
          window.location.replace("/");
        }
        return;
      }

      window.location.reload();
    }
  };

  if (!setInviteOpen) {
    return (
      <>
        {isAuthenticated ? (
          <div>
            <div className="w-1/2 m-auto h-full flex flex-col justify-center gap-8">
              <Heading className="mt-60">Keine Einladung gefunden</Heading>
              <Button
                onClick={() => (window.location.href = "/")}
                colorScheme="primary"
                className="flex gap-2 p-2 text-white"
                variant="solid"
              >
                <span>Zurück zum Startbildschirm</span>
              </Button>
            </div>
          </div>
        ) : (
          <></>
        )}
      </>
    );
  }

  return (
    <>
      {!inviteIsLoading ? (
        <>
          {inviteRes ? (
            <Dialog open={isInviteOpen} onOpenChange={(value) => setInviteOpen(value)}>
              <DialogContent className="z-50" isClosable={false}>
                <DialogHeader>
                  <DialogTitle>Einladung</DialogTitle>
                </DialogHeader>
                <div>
                  <p>
                    {inviteRes.alreadyHasOrganization
                      ? `Du hast bereits eine Organisation. Willst du deine aktuelle verlassen um ${inviteRes.organizationName} beizutreten?`
                      : `Du wurdest eingeladen ${inviteRes.organizationName} beizutreten.`}
                    <br />
                    <span className="italic">Man kann nur einer Organisation angehören.</span>
                  </p>
                </div>
                <DialogFooter>
                  <div className={`flex gap-3 w-full`}>
                    <Button colorScheme="blue" className="grow" onClick={() => handleInvite("accept")}>
                      Einladung annehmen
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : (
            <div>
              <div className="w-1/2 m-auto h-full flex flex-col justify-center gap-8">
                <Heading className="mt-60">Keine Einladung gefunden</Heading>
                <Button
                  onClick={() => (window.location.href = "/")}
                  colorScheme="primary"
                  className="flex gap-2 p-2 text-white"
                  variant="solid"
                >
                  <span>Zurück zum Startbildschirm</span>
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <Spinner />
      )}
    </>
  );
};

export { Invitation };
